import { ModeNamesEnum } from './browse-mode';
import { BrowseTypesEnum } from './browse-types.enum';
import { Sport, SportsEnum } from './sport';
import { Tag } from './tag.interface';
import { Vehicle } from './vehicle.interface';
import { WORLD_REGIONS, WorldRegion } from './world-region';

export interface FilterOption {
  value: string;
  label: string;
  for?: string[];
  isCustom?: boolean;
}

export const getSearchModeFilterOptions = (): FilterOption[] => {
  return [
    {
      label: `browse.toolbar.${ModeNamesEnum.Videos}`,
      value: BrowseTypesEnum.Video,
    },
    {
      label: `browse.toolbar.${ModeNamesEnum.Coachings}`,
      value: BrowseTypesEnum.Coaching,
    },
  ];
};

export const getWorkoutTypeFilterOptions = (sport: Sport): FilterOption[] => {
  switch (sport.name) {
    case SportsEnum.Cycling:
      return [
        {
          label: 'FTP',
          value: 'ftp',
        },
        {
          label: 'RES',
          value: 'res',
        },
        {
          label: 'SLOPE',
          value: 'slope',
        },
        {
          label: 'WATTS',
          value: 'watts',
        },
      ];
    case SportsEnum.Running:
      return [
        {
          label: 'RES',
          value: 'res',
        },
        {
          label: 'INC',
          value: 'inc',
        },
        {
          label: 'SPD',
          value: 'spd',
        },
        {
          label: 'INC + SPD',
          value: 'incSpd',
        },
      ];
    case SportsEnum.Rowing:
      return [
        {
          label: 'RES',
          value: 'res',
        },
      ];
  }
};

export const getDurationFilterOptions = (): FilterOption[] => {
  return [
    {
      label: 'browse.filterSort.filters.duration.options.under',
      value: '0-20',
    },
    {
      label: 'browse.filterSort.filters.duration.options.between',
      value: '20-30',
    },
    {
      label: 'browse.filterSort.filters.duration.options.between',
      value: '30-45',
    },
    {
      label: 'browse.filterSort.filters.duration.options.over',
      value: '45-0',
    },
  ];
};

export const getDistanceFilterOptions = (): FilterOption[] => {
  return [
    {
      label: 'browse.filterSort.filters.distance.options.under',
      value: '0-20',
    },
    {
      label: 'browse.filterSort.filters.distance.options.between',
      value: '20-40',
    },
    {
      label: 'browse.filterSort.filters.distance.options.between',
      value: '40-60',
    },
    {
      label: 'browse.filterSort.filters.distance.options.between',
      value: '60-100',
    },
    {
      label: 'browse.filterSort.filters.distance.options.over',
      value: '100-0',
    },
    {
      label: 'browse.filterSort.filters.distance.options.custom',
      value: '0-100000',
      isCustom: true,
    },
  ];
};

export const getSlopeFilterOptions = (): FilterOption[] => {
  return [
    {
      label: 'browse.filterSort.filters.slope.options.between',
      value: '0-2000',
    },
  ];
};

export const getDifficultyFilterOptions = (): FilterOption[] => {
  return [
    {
      label: 'browse.filterSort.filters.difficulty.options.easy',
      value: 'easy',
    },
    {
      label: 'browse.filterSort.filters.difficulty.options.medium',
      value: 'medium',
    },
    {
      label: 'browse.filterSort.filters.difficulty.options.hard',
      value: 'hard',
    },
    {
      label: 'browse.filterSort.filters.difficulty.options.expert',
      value: 'expert',
    },
  ];
};

export const getQualityFilterOptions = (): FilterOption[] => {
  return [
    {
      label: 'browse.filterSort.filters.quality.options.bad',
      value: 'bad',
    },
    {
      label: 'browse.filterSort.filters.quality.options.medium',
      value: 'medium',
    },
    {
      label: 'browse.filterSort.filters.quality.options.good',
      value: 'good',
    },
    {
      label: 'browse.filterSort.filters.quality.options.excellent',
      value: 'stabilized',
    },
  ];
};

export const getProgressFilterOptions = (): FilterOption[] => {
  return [
    {
      label: 'browse.filterSort.filters.progress.options.hideCompletedVideos',
      value: 'excludeComplete',
    },
    {
      label: 'browse.filterSort.filters.progress.options.hideStartedVideos',
      value: 'excludeStarted',
    },
  ];
};

export const getSoundFilterOptions = (): FilterOption[] => {
  return [
    {
      label: 'browse.filterSort.filters.sound.options.with',
      value: 'true',
    },
    {
      label: 'browse.filterSort.filters.sound.options.without',
      value: 'false',
    },
  ];
};

export const getSpeedFilterOptions = (): FilterOption[] => {
  return [
    {
      label: 'browse.filterSort.filters.speed.options.between',
      value: '0-50',
    },
  ];
};

export const getPowerFilterOptions = (): FilterOption[] => {
  return [
    {
      label: 'browse.filterSort.filters.power.options.under',
      value: '0-50',
    },
    {
      label: 'browse.filterSort.filters.power.options.between',
      value: '50-100',
    },
    {
      label: 'browse.filterSort.filters.power.options.between',
      value: '100-150',
    },
    {
      label: 'browse.filterSort.filters.power.options.between',
      value: '150-200',
    },
    {
      label: 'browse.filterSort.filters.power.options.over',
      value: '200-0',
    },
  ];
};

export const getTagFilterOptions = (tags: Tag[]): FilterOption[] => {
  return tags.map((tag: Tag) => {
    return {
      label: tag.name,
      value: tag.id.toString(),
    };
  });
};

export const getSortDirection = (): FilterOption[] => {
  return [
    {
      label: 'asc',
      value: 'asc',
    },
    {
      label: 'desc',
      value: 'desc',
    },
  ];
};

export const getLangFilterOptions = (): FilterOption[] => {
  return [
    {
      label: 'browse.languages.en',
      value: '1',
    },
    {
      label: 'browse.languages.de',
      value: '2',
    },
    {
      label: 'browse.languages.fr',
      value: '3',
    },
    {
      label: 'browse.languages.es',
      value: '4',
    },
    {
      label: 'browse.languages.it',
      value: '5',
    },
    // {
    //   label: 'browse.languages.nl',
    //   value: '6',
    // },
    // {
    //   label: 'browse.languages.pt',
    //   value: '8',
    // },
    // {
    //   label: 'browse.languages.sl',
    //   value: '50',
    // },
  ];
};

export const getEquipmentFilterOptions = (sport: Sport): FilterOption[] => {
  const options = [
    {
      label: 'browse.filterSort.filters.equipment.options.bike',
      value: 'exerciseBike',
      for: [SportsEnum.Cycling],
    },
    {
      label: 'browse.filterSort.filters.equipment.options.hometrainer',
      value: 'homeTrainer',
      for: [SportsEnum.Cycling],
    },
    {
      label: 'browse.filterSort.filters.equipment.options.treadmill',
      value: 'treadmill',
      for: [SportsEnum.Running],
    },
    {
      label: 'browse.filterSort.filters.equipment.options.elliptical',
      value: 'elliptical',
      for: [SportsEnum.Running],
    },
    {
      label: 'browse.filterSort.filters.equipment.options.rower',
      value: 'rowingMachine',
      for: [SportsEnum.Rowing],
    },
  ];

  return options
    .filter((option: FilterOption) => option.for?.includes(sport.name))
    .map(({ for: _, ...rest }) => rest);
};

export const getInAppFilterOptions = (): FilterOption[] => {
  return [
    {
      label: 'browse.filterSort.filters.inApp.options.availableInApp',
      value: '1',
    },
  ];
};

export const getClimbCategoryFilterOptions = (): FilterOption[] => {
  return [...Array(6).keys()].map((i) => {
    return { label: i.toString(), value: i.toString() };
  });
};

export const getVehicleFilterOptions = (
  vehicles: Vehicle[]
): FilterOption[] => {
  return vehicles.map((vehicle: Vehicle) => {
    return {
      label: vehicle.name,
      value: vehicle.id.toString(),
    };
  });
};

export const getRegionFilterOptions = (): FilterOption[] => {
  const regions: WorldRegion[] = WORLD_REGIONS;
  let filterOptions: FilterOption[] = [];
  regions.forEach((region: WorldRegion) => {
    const option: FilterOption = {
      value: region.code.toString(),
      label: region.name,
    };
    filterOptions.push(option);
  });
  return filterOptions;
};
